import { Fragment, useState } from "react";
import { ChevronDown, ScrollText } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useServerList } from "@/hooks/useServerList";
import { fmtDateTime } from "@/lib/i18n";
import { EmptyState, Pager, Panel, RowsSkeleton, SearchBox, StatusBadge } from "@/components/app/kit";

const ACTIONS = [
  ["all", "Toutes les actions"], ["auth", "Authentification"], ["cabinet", "Cabinets"], ["utilisateur", "Utilisateurs"],
  ["invitation", "Invitations"], ["role", "Rôles"], ["patient", "Patients"], ["domaine", "Domaines"], ["module", "Modules"],
  ["formule", "Formules"], ["admin_plateforme", "Administrateurs"], ["parametres", "Paramètres"],
];

export const AuditTable = ({ path, showTenant }) => {
  const list = useServerList(path, { action: "all", result: "all" }, 25);
  const [open, setOpen] = useState(null);
  return (
    <Panel className="overflow-hidden" testid="audit-panel">
      <div className="flex flex-col sm:flex-row gap-3 -mt-1 mb-4">
        <SearchBox value={list.filters.search} onChange={(v) => list.setFilter("search", v)} placeholder="Utilisateur, action, IP…" testid="audit-search" />
        <Select value={list.filters.action} onValueChange={(v) => list.setFilter("action", v)}>
          <SelectTrigger className="sm:w-56 bg-white" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{ACTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={list.filters.result} onValueChange={(v) => list.setFilter("result", v)}>
          <SelectTrigger className="sm:w-40 bg-white" data-testid="audit-result-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous résultats</SelectItem>
            <SelectItem value="success">Succès</SelectItem>
            <SelectItem value="failure">Échecs</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="-mx-5 overflow-x-auto">
        {list.loading ? <RowsSkeleton /> : list.items.length === 0 ? (
          <EmptyState icon={ScrollText} title="Aucun événement" text="Aucune entrée ne correspond aux filtres." />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead><TableHead>Utilisateur</TableHead><TableHead>Action</TableHead>
                <TableHead>Entité</TableHead>{showTenant && <TableHead>Cabinet</TableHead>}<TableHead>IP</TableHead><TableHead>Résultat</TableHead><TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.items.map((a) => (
                <Fragment key={a.id}>
                  <TableRow className="cursor-pointer" onClick={() => setOpen(open === a.id ? null : a.id)} data-testid="audit-row">
                    <TableCell className="text-xs whitespace-nowrap text-slate-500">{fmtDateTime(a.at)}</TableCell>
                    <TableCell className="text-sm">{a.actor_email || <span className="text-slate-400">anonyme</span>}</TableCell>
                    <TableCell><span className="font-mono text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded border border-slate-200">{a.action}</span></TableCell>
                    <TableCell className="text-xs text-slate-500">{a.entity_type}</TableCell>
                    {showTenant && <TableCell className="font-mono text-[11px] text-slate-400">{a.tenant_id ? a.tenant_id.slice(-8) : "—"}</TableCell>}
                    <TableCell className="font-mono text-xs text-slate-500">{a.ip || "—"}</TableCell>
                    <TableCell><StatusBadge status={a.result} prefix="result" /></TableCell>
                    <TableCell><ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open === a.id ? "rotate-180" : ""}`} /></TableCell>
                  </TableRow>
                  {open === a.id && (
                    <TableRow>
                      <TableCell colSpan={8} className="bg-slate-50">
                        <pre className="text-xs font-mono text-slate-600 whitespace-pre-wrap" data-testid="audit-details">{JSON.stringify(a.details, null, 2)}</pre>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      <div className="-mx-5 -mb-5"><Pager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} /></div>
    </Panel>
  );
};
